import React, { useEffect, useMemo, useRef, useState } from 'react';

const normalize = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const compact = (value = '') => normalize(value).replace(/\s+/g, '');

const matchesItem = (item, rawQuery) => {
  const q = normalize(rawQuery);
  if (!q) return true;
  const haystack = normalize([item.label, item.id, ...(item.keywords || [])].filter(Boolean).join(' '));
  const hayCompact = compact(haystack);
  const qCompact = compact(rawQuery);
  if (haystack.includes(q) || (qCompact && hayCompact.includes(qCompact))) return true;
  return q.split(/\s+/).filter(Boolean).every((token) => haystack.includes(token) || hayCompact.includes(token));
};

/**
 * Generic searchable context dropdown (workspace / project).
 */
const ContextSelect = ({
  label,
  valueLabel,
  items = [],
  onSelect,
  emptyLabel = 'No items',
  placeholder = 'Search…',
  onSearch,
  loading = false,
  footer,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
        onSearch?.('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onSearch]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return items;
    return items.filter((item) => matchesItem(item, q));
  }, [items, query]);

  const handleQueryChange = (value) => {
    setQuery(value);
    onSearch?.(value);
  };

  return (
    <div className="rt2-context-select" ref={rootRef}>
      <button
        type="button"
        className="rt2-context-select__btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="rt2-context-select__label">{label}</span>
        <span className="rt2-context-select__value" title={valueLabel}>
          {valueLabel || 'Select…'}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="rt2-context-select__menu" role="listbox">
          <input
            className="rt2-context-select__search"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={placeholder}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
          {loading && <div className="rt2-empty" style={{ padding: 12 }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="rt2-empty" style={{ padding: 12 }}>
              {query.trim() ? `No matches for “${query.trim()}”` : emptyLabel}
            </div>
          )}
          <div className="rt2-context-select__list">
            {filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                role="option"
                className={`rt2-context-select__item${item.active ? ' rt2-context-select__item--active' : ''}`}
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                  setQuery('');
                  onSearch?.('');
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          {(footer || items.length > 0) && (
            <div className="rt2-context-select__footer">
              {footer || (
                <span>
                  {filtered.length === items.length
                    ? `${items.length} total`
                    : `${filtered.length} of ${items.length}`}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContextSelect;
